import { Button, Text } from "../uikit";
import Card from "./Card";
import Icon from "./Icon";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import DevicesIcon from "../img/devices.svg";
import TariffIcon from "../img/tariff.svg";
import ExpirationIcon from "../img/expiration.svg";
import LoginIcon from "../img/login.svg";
import PasswordIcon from "../img/password.svg";
import EditIcon from "../img/edit.svg";
import LockIcon from "../img/lock.svg";
import NetworkIcon from "../img/network.svg";
import { formatExitTargetLabel, getCountryEmoji } from "../utils/serverDisplay";

export default function SubscriptionCard({
  row,
  onlineCount = 0,
  isOnlineCountLoading = false,
}) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const exitTargetLabel = formatExitTargetLabel(row, i18n.resolvedLanguage);
  const exitTargetFlag = getCountryEmoji(row.last_exit_server_country_code);
  const exitTargetText =
    exitTargetFlag && exitTargetLabel.startsWith(exitTargetFlag)
      ? exitTargetLabel.slice(exitTargetFlag.length).trimStart()
      : exitTargetLabel;
  const tariffName = String(row.tariff?.name ?? "").trim();
  const formattedTariffName = tariffName
    ? tariffName.charAt(0).toUpperCase() + tariffName.slice(1)
    : "";
  const formatExpirationDate = (expirationDate) => {
    if (!expirationDate) {
      return "";
    }
    return String(expirationDate).slice(0, 10);
  };

  const getOnlineConnectionsLabel = (count) => {
    const absCount = Math.abs(Number(count) || 0);
    const mod10 = absCount % 10;
    const mod100 = absCount % 100;

    if (mod10 === 1 && mod100 !== 11) {
      return `${absCount} ${t("connection online one")}`;
    }
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
      return `${absCount} ${t("connection online few")}`;
    }
    return `${absCount} ${t("connection online many")}`;
  };

  return (
    <Card>
      <div className="column card">
        <div className="row header">
          <div className="column">
            <Text weight="2">
              {row.is_demo ? t("Demo subscription") : t("Subscription")}
            </Text>
          </div>
          <Button
            mode="plain"
            size="s"
            onClick={() =>
              navigate("/account/subscription", {
                state: {
                  subscription: row,
                },
              })
            }
          >
            {t("Prolong")}
          </Button>
        </div>
        <div className="row">
          <img src={LoginIcon} alt="" className="monochrome-icon" />
          <div className="ml-05">
            <Text>
              {t("login3")} <span style={{ fontWeight: 700 }}>{row.login}</span>
            </Text>
          </div>
        </div>
        <div className="row">
          <img src={PasswordIcon} alt="" className="monochrome-icon" />
          <div className="ml-05">
            <Text>
              {t("Password")}{" "}
              <span style={{ fontWeight: 700 }}>{row.password}</span>
            </Text>
          </div>
        </div>
        {row.expire_in_days > 0 ? (
          <>
            {row.tariff?.name ? (
              <div className="row">
                <img src={TariffIcon} alt="" className="monochrome-icon" />
                <div className="ml-05">
                  <Text>
                    {t("Tariff")}{" "}
                    <span style={{ fontWeight: 700 }}>{formattedTariffName}</span>
                  </Text>
                </div>
              </div>
            ) : (
              <></>
            )}
            <div className="row">
              <img src={ExpirationIcon} alt="" className="monochrome-icon" />
              <div className="ml-05">
                <Text>
                  {row.expire_in_days} {row.expire_in_days == 1 ? t("day") : ""}
                  {row.expire_in_days >= 2 && row.expire_in_days <= 4
                    ? t("days")
                    : ""}
                  {row.expire_in_days >= 5 ? t("days2") : ""}{" "}
                  {t("until expiration")}
                </Text>
              </div>
            </div>
            <div className="row">
              <img src={DevicesIcon} alt="" className="monochrome-icon" />
              {row.tariff.connections ? (
                <div className="ml-05">
                  <Text>
                    {row.tariff.connections}{" "}
                    {row.tariff.connections == 1 ? t("device") : ""}
                    {row.tariff.connections >= 2 && row.tariff.connections <= 4
                      ? t("devices")
                      : ""}
                    {row.tariff.connections >= 5 || row.tariff.connections <= 0
                      ? t("devices2")
                      : ""}
                  </Text>
                </div>
              ) : (
                <div></div>
              )}
            </div>
            {(row.private_addresses || []).length > 0 ? (
              <div className="row">
                <div>
                  <img src={LockIcon} alt="" className="monochrome-icon" />
                </div>
                <div className="ml-05 column">
                  <Text>{t("Private IP addresses")}</Text>
                  {(row.private_addresses || []).map((item) => (
                    <Text key={item.address}>
                      <span style={{ fontWeight: 700 }}>{item.address}</span>
                    </Text>
                  ))}
                </div>
              </div>
            ) : (
              <></>
            )}
            <div
              className="row vertical-center"
              style={{ gap: "calc(var(--distance) / 2)" }}
            >
              <div
                style={{ width: 24, textAlign: "center", flexShrink: 0 }}
              >
                {exitTargetFlag || <img src={NetworkIcon} alt="" className="monochrome-icon"/>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text>{exitTargetLabel ? exitTargetText : "—"}</Text>
              </div>
              {/*<img
                src={EditIcon}
                alt=""
                className="pointer"
                style={{ width: 24, height: 24, flexShrink: 0 }}
                onClick={() =>
                  navigate("/account/subscription-exit", {
                    state: { subscription: row, mode: "subscription" },
                  })
                }
              />*/}
            </div>
            <Button
              mode="filled"
              size="l"
              className="row"
              onClick={() =>
                navigate(`/account/guide/${row.login}`, {
                  state: { subscription: row },
                })
              }
            >
              {t("Connect")}
            </Button>
            <Button
              mode="outline"
              size="l"
              className="row"
              before={
                <img src={EditIcon} alt=""/>
              }
              onClick={() =>
                onlineCount === 0
                  ? navigate("/account/subscription-exit", {
                      state: { subscription: row, mode: "subscription" },
                    })
                  : navigate("/account/online", {
                      state: { subscription: row },
                    })
              }
            >
              {t("Change location")}
            </Button>
            <Button
              mode="outline"
              size="l"
              className="row"
              loading={isOnlineCountLoading}
              disabled={isOnlineCountLoading}
              onClick={() =>
                navigate("/account/online", {
                  state: { subscription: row },
                })
              }
            >
              {getOnlineConnectionsLabel(onlineCount)}
            </Button>
          </>
        ) : (
          <>
            <div className="row">
              <img src={ExpirationIcon} alt="" className="monochrome-icon" />
              <div className="ml-05">
                <Text>
                  {t("Expired")} {formatExpirationDate(row.expiration_date)}
                </Text>
              </div>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
